import type { WorkOrderStatusSystemName } from '../job/workorder.status.system.name';
import type { SystemPriority } from '../shared/system.priority';

export enum AISettingsAction {
  GetGeneralSettings = 'GetAIGeneralSettingsQuery',
  UpdateGeneralSettings = 'UpdateAIGeneralSettingsCommand',
  GetTaskValidationSettings = 'GetAITaskValidationSettingsQuery',
  UpdateTaskValidationSettings = 'UpdateAITaskValidationSettingsCommand',
  GetBillImportSettings = 'GetAIBillImportSettingsQuery',
  UpdateBillImportSettings = 'UpdateAIBillImportSettingsCommand',
  GetEffectiveSettings = 'GetAIEffectiveSettingsQuery',
  GetWorkbuddyAgentSettings = 'GetAIWorkbuddyAgentSettingsQuery',
  UpdateWorkbuddyAgentSettings = 'UpdateAIWorkbuddyAgentSettingsCommand',
  GetCachedAgentWorkspaceStatus = 'GetCachedAIAgentWorkspaceStatusQuery',
  VerifyAgentWorkspaceStorage = 'VerifyAIAgentWorkspaceStorageCommand',
  EnsureAgentWorkspaceStorage = 'EnsureAIAgentWorkspaceStorageCommand',
}

export interface CriteriaFilterRule {
  fieldId: string;
  operator: string;
  value?: unknown;
}

export interface CriteriaFilterGroup {
  operator: 'and' | 'or';
  rules: (CriteriaFilterRule | CriteriaFilterGroup)[];
}

export interface AIValidationCriteria {
  id: string;
  name: string;
  instruction: string;
  enabled: boolean;
  isDefault?: boolean;
  conditions?: CriteriaFilterGroup;
  notifyOnFail?: boolean;
}

export interface AIAgents {
  taskValidation?: boolean;
  billImport?: boolean;
}

export interface AIEffectiveSettings {
  enabled: boolean;
  agents: AIAgents;
}

export type AIAgentWorkspaceStorageStatus = 'ready' | 'not_set_up' | 'setting_up' | 'needs_attention';

export const AI_AGENT_WORKSPACE_STORAGE_STATUSES: readonly AIAgentWorkspaceStorageStatus[] = ['ready', 'not_set_up', 'setting_up', 'needs_attention'] as const;

/**
 * Shared route contract between jms-api (caller) and jms-ai (Mastra route owner).
 * Always reference these paths via the constant rather than typing the literal
 * — `setupAgentWorkspaceStorageRoute` in jms-ai registers the same path.
 */
export const AGENT_WORKSPACE_STORAGE_ROUTES = {
  check: '/agent-workspace/storage/check',
  setup: '/agent-workspace/storage/setup',
} as const;

export interface AIAgentWorkspaceStatus {
  status: AIAgentWorkspaceStorageStatus;
  checkedAt?: string;
  message?: string;
}

/**
 * Persisted runtime state for the per-tenant agent workspace bucket.
 *
 * Acts as a tenant-scoped lock + cache around provisioning so concurrent callers
 * (e.g. multiple users opening the agent tab at the same time) share one
 * in-flight setup and avoid redundant GCS round-trips when the bucket is ready.
 */
export interface AIAgentWorkspaceState {
  status: AIAgentWorkspaceStorageStatus;
  startedAt?: string;
  completedAt?: string;
  message?: string;
}

export interface AIGeneralSetting {
  enabled?: boolean;
  agents?: AIAgents;
  textRefinerEnabled?: boolean;
  voiceToTextEnabled?: boolean;
}

export interface AITaskValidationSetting {
  // Unused fields kept for reference only (not wired into any handler or UI).
  // generalInstruction?: string;
  criteria?: AIValidationCriteria[];
  // allowRetryOnFail?: boolean;
  // maxRetries?: number;
  // autoRejectTask?: boolean;
  // autoRejectMinPoints?: number;
}

export interface AIBillImportSetting {
  defaultUnknownItem?: string;
}

export interface WorkbuddyAgentStageOperationsSetting {
  blockedStageOwnerTypes?: Array<'Employee' | 'Team'>;
  /** Legacy allow-list setting kept so existing tenants preserve behaviour until the next save. */
  stageOwnerAllowedTypes?: Array<'Employee' | 'Team'>;
  subcontractorAssignmentMode?: 'strict' | 'lenient' | 'disabled';
}

export interface WorkbuddyAgentStageStatusSetting {
  blockedTargets?: string[];
  /** Legacy allow-list setting kept so existing tenants preserve behaviour until the next save. */
  allowedTargets?: WorkOrderStatusSystemName[];
}

export interface WorkbuddyAgentJobFieldsSetting {
  maxPriority?: SystemPriority | null;
  minDemotionPriority?: SystemPriority | null;
  blockedJobTypeIds?: string[];
  blockedLabelIds?: string[];
  blockAlertLabels?: boolean;
  blockJobRenaming?: boolean;
  /** Legacy capability setting kept so existing tenants preserve behaviour until the next save. */
  protectAlertLabels?: boolean;
  /** Legacy capability setting kept so existing tenants preserve behaviour until the next save. */
  allowRenamingJobs?: boolean;
}

export interface WorkbuddyAgentJobSchedulingSetting {
  maxScheduleHorizonDays?: number;
  minScheduleLeadHours?: number;
  maxPullInDays?: number | null;
  maxPushOutDays?: number | null;
  clampDueDatesToBusinessHours?: boolean;
  avoidNonWorkingDays?: boolean;
}

export interface WorkbuddyAgentJobAssignmentsSetting {
  [key: string]: unknown;
}

export interface WorkbuddyAgentAddLineItemsSetting {
  stagePlacement?: 'prefer_existing' | 'always_new';
  explicitStagePolicy?: 'match_category' | 'force_stage';
}

export interface WorkbuddyAgentReadDocumentsSetting {
  includeRelatedDefault?: boolean;
}

/** Upper bound on workspace-wide agent instructions, so the cached prompt prefix stays small. */
export const AGENT_TENANT_INSTRUCTIONS_MAX_LENGTH = 4000;

export interface WorkbuddyAgentConfigs {
  /**
   * Workspace-wide instructions from the tenancy's administrators, given to the agent once per
   * thread as a hidden user-level message (never the system prompt). Only users who can manage
   * AI settings read the text; everyone else gets `tenantInstructionsSet` instead.
   */
  tenantInstructions?: string;
  /** On reads by users who cannot manage AI settings: whether instructions exist; their text is withheld. Never stored. */
  tenantInstructionsSet?: boolean;
  addLineItems?: WorkbuddyAgentAddLineItemsSetting;
  readDocuments?: WorkbuddyAgentReadDocumentsSetting;
  stageOperations?: WorkbuddyAgentStageOperationsSetting;
  stageStatus?: WorkbuddyAgentStageStatusSetting;
  jobFields?: WorkbuddyAgentJobFieldsSetting;
  jobScheduling?: WorkbuddyAgentJobSchedulingSetting;
  jobAssignments?: WorkbuddyAgentJobAssignmentsSetting;
}

export interface AISetting {
  general?: AIGeneralSetting;
  taskValidation?: AITaskValidationSetting;
  documentExtraction?: AIBillImportSetting;
  workbuddyAgent?: WorkbuddyAgentConfigs;
}

export const DEFAULT_AI_VALIDATION_CRITERIA: AIValidationCriteria[] = [
  // IMPORTANT: Criteria ids are persisted into tenant settings and are used to map a tenant's saved criteria back to the system default.
  // Do not rename or replace existing ids after a default has been seeded. Add a new criterion id instead, or provide an explicit legacy-id migration.
  { id: 'expectedLocationMatch', name: 'Expected Location Match', instruction: `The photos provided with the SOR are identified as approximately matching the expected location of the SOR. Items visible in said images are generally a good indicator of the location type match.

Below are some illustrative examples about the expected images given the work type in the SORs and then the expected visible items in the scene:
- Plumbing works relating to a kitchen sink should show images related to indoor plumbing (kitchen sink, indoor tap, other indoor plumbing, etc) not images related to outdoor plumbing (sprinklers, garden hoses, gutters, etc)
- External cleaning works should show images related to external of the house (gardens, lawns, trees) not internal items (carpet, floorboards, kitchen bench)
- Electrical works should show electrical items (power points, light switches, switch/circuit boards, RCDs)
- Landscaping works should show landscaping items (lawns, gardens, trees)

As an example, if the SOR descriptions mention a specific type of work like electrical work, but the photos ONLY include plumbing images, this criteria should fail. Some leniency should be provided if images include adjacent work that has been completed.

If the photo is not clear enough to distinguish the location effectively, this criteria should also not pass.`, enabled: true, isDefault: true, notifyOnFail: true },
  { id: 'workTypeMatch', name: 'Work Type Match', instruction: `Photos provided with the SOR are identified as matching or not matching the type of work that the SOR is dictating. The work type is going to be something categorical such as "Electrical", "Roofing", "Cleaning", etc. The evidence submitted should demonstrate that the technician's work was completed in such an environment.

For example:
- Plumbing works show items related to plumbing (pipes, taps, hoses)
- Electrical works show items related to electrical (power points, light switches, switch/circuit boards, RCDs)
- Landscaping works show items related to landscaping (lawns, gardens, trees).

This criteria is not about validating exactly what was done, but rather to ensure that the evidence submitted matches what might be submitted to prove work that has been done for the given Work Type.`, enabled: true, isDefault: true, notifyOnFail: true },
  { id: 'workCompletion', name: 'Work Completion', instruction: `The submitted evidence must demonstrate to a reasonable extent that the specified work required for the SOR code has been completed. This validation must try to reason from the submitted evidence photos by contrasting the before and after photos to ensure that the evidence demonstrates that work has been completed with visible changes or proof of a resolution in the photo.

Due to the nature of the evidence submitted (as photos), some visual reasoning must be done to infer exactly what the technician's intent was with regards to what they try to demonstrate, and then validate whether that demonstrated piece of work effectively provides evidence for completion of the task described in the SOR.

Note that there are likely to be certain types of criteria that are not necessarily "verifiable" through images alone such as testing a circuit. For such cases, the photos must show demonstrably different images of the scene to some extent rather than duplicate photos or only a before and no after photo. The minimum requirement for such non-verifiable jobs is that the photos are at least not duplicates.`, enabled: true, isDefault: true, notifyOnFail: true },
  { id: 'timestampPresence', name: 'Timestamp Presence', instruction: `Every photo submitted must have a timestamp visible in the photo. If no visible timestamp can be found, the criteria is not met.

In addition to the timestamp presence:
- The timestamp must be dated after the relevant work order/stage start date when that date is available. If no stage start date is available, use the job first dispatch date or job start date as supporting context.
- At least one timestamp on the images should be within the required completion timeframe specified for the stage or task when those due/completion dates are available.`, enabled: true, isDefault: true, notifyOnFail: true },
  { id: 'duplicatePhotos', name: 'Duplicate Photos', instruction: `If an identical duplicate photo is present, the evidence submission is invalid. All photos submitted must be unique.

If a photo is submitted which shows signs of being an identical photo but cropped, rotated or otherwise slightly manipulated to bypass filters, this criteria will also not be met and the task validation must fail.`, enabled: true, isDefault: true, notifyOnFail: true },
  { id: 'beforeAfterSequence', name: 'Before/After Photo Sequence', instruction: 'When before/after photos are submitted or the task expects progression evidence, verify that the submitted before photos visually represent the initial or uncompleted state and submitted after photos visually represent the completed or improved state. Use submitted phase metadata as context for how the photos were provided in WorkBuddy. Use visible timestamps only as supporting evidence when they are legible and relevant. Fail when the visible work state clearly contradicts the submitted phase sequence, such as before photos showing completed work while after photos show uncompleted work. Mark not applicable when the task does not require progression evidence and the submission does not include both before and after phase photos.', enabled: false, isDefault: true, notifyOnFail: true },
  { id: 'photoQuality', name: 'Photo Quality', instruction: 'Flag technical issues that impede validation. Be reasonable about quality requirements considering the environment the technician works in. Check for severely blurry images, rotated images, important subjects obstructed or out of frame, inappropriate content, and images too dark or bright to assess.', enabled: false, isDefault: true, notifyOnFail: true },
];
